"use strict";
/**
 * Entity Filename Parsing — Shared Utility
 *
 * Canonical implementation of multi-entity filename parsing.
 * Used by both admin-functions and client-functions for Loan Market
 * entity-coded filenames (e.g., "ENTO100 AGMT RUN 1 Nov 25.xlsx").
 *
 * Consolidates 3 previously duplicated inline copies.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractEntityCode = extractEntityCode;
exports.extractEntityCodeLoose = extractEntityCodeLoose;
exports.parseEntityFilename = parseEntityFilename;
exports.groupFilesByEntity = groupFilesByEntity;
exports.formatPeriodMonth = formatPeriodMonth;
exports.extractEntityCodeFromEmailFilename = extractEntityCodeFromEmailFilename;
/**
 * Extract entity code from filename.
 * Entity codes are uppercase alphanumeric prefixes (e.g., ENTO100, ENTM100, ENBB100).
 *
 * Dotted anchors (2026-08-28)
 * ---------------------------
 * Aggregators emit the same code with and without interior dots (`MAVI100` and
 * `MAVI1.00`, `BRIS302` and `BRIS3.02`) for the same entity. The dot is
 * cosmetic, so it is stripped before the digit post-check and the returned code
 * is always the undotted canonical form.
 *
 * This previously matched `[A-Z][A-Z0-9]{1,9}` with no dot in the class, so
 * `"MAVI1.00 AGMT RUN 3 Jul 23.xlsx"` failed to match at all (`MAVI1` is
 * followed by `.`, not whitespace) and returned null. On a unified-mode tenant
 * that null propagates to `upload_intents.entityCode`, then to a
 * `client_processing_queue` document with the field absent, then to
 * `raw_commissions.entity_code = NULL` and an `office:{officeId}` reservation
 * key instead of an entity-scoped one. That is the loan-market-capital defect of
 * 2026-08-18; see docs/handovers/2026-08-22-BIGQUERY-DML-CEILING-ENTITY-CODE-LOSS.md.
 *
 * The ingress detector in `issueSignedUploadUrl.ts` was already dot-aware, which
 * is why the client upload path never lost a code and the staged/admin path did.
 * Both now share this one implementation.
 *
 * Bounded quantifier {1,12} avoids ReDoS: anchored, no nested repetitions.
 */
function extractEntityCode(filename) {
    // Matches entity codes like ENTO100, ENTM100, W4FI104, BRIS302, MAVI1.00 etc.
    const match = filename.match(/^([A-Z][A-Z0-9.]{1,12})\s/);
    if (!match)
        return null;
    // Dots are cosmetic separators: MAVI1.00 and MAVI100 are the same entity.
    const stripped = match[1].replace(/\./g, '');
    // Post-check ensures code ends with a digit (excludes plain words like AMAG).
    return /\d$/.test(stripped) ? stripped : null;
}
/**
 * Delimiter-tolerant anchor extraction for SANITIZED filenames.
 *
 * The admin ZIP and EML ingestion paths receive names whose spaces have already
 * become underscores (`TCSF100_AGMT_RUN_1.xlsx`), which {@link extractEntityCode}
 * deliberately does NOT match: the logical-key path depends on that helper
 * failing on the sanitized form (see reserveUploadLogicalKey.test.ts).
 *
 * So this is a SEPARATE function rather than a widened `extractEntityCode`.
 * It mirrors what those two paths did inline before 2026-08-28, byte for byte,
 * with one change: dots in the anchor are stripped, so `MAVI1.00_AGMT_...`
 * resolves to `MAVI100` instead of null.
 *
 * NOTE the digit rule differs from `extractEntityCode` on purpose. This one
 * requires the code to CONTAIN a digit; `extractEntityCode` requires it to END
 * with one. That divergence is inherited from the call sites and is preserved
 * here so this change is provably zero-blast for undotted names. Unifying the
 * two rules is a deliberate follow-up, not a side effect of a dot fix.
 */
function extractEntityCodeLoose(filename) {
    const match = filename.match(/^([A-Z][A-Z0-9.]{1,12})[_\s]/);
    if (!match)
        return null;
    const stripped = match[1].replace(/\./g, '');
    return /\d/.test(stripped) ? stripped : null;
}
/**
 * Parse full filename into structured components.
 */
function parseEntityFilename(filename) {
    const entityCode = extractEntityCode(filename);
    let fileType = 'unknown';
    if (filename.includes(' AGMT '))
        fileType = 'AGMT';
    else if (filename.includes(' LW '))
        fileType = 'LW';
    else if (filename.includes(' MEM '))
        fileType = 'MEM';
    else if (filename.toLowerCase().includes('summary'))
        fileType = 'Summary';
    const runMatch = filename.match(/RUN\s*(\d+)/i);
    const runNumber = runMatch ? parseInt(runMatch[1], 10) : null;
    const monthMatch = filename.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/i);
    const month = monthMatch ? monthMatch[1] : null;
    const yearMatch = filename.match(/\b(20\d{2}|\d{2})\b(?=\.[a-z]+$)/i);
    const year = yearMatch ? yearMatch[1] : null;
    const extMatch = filename.match(/\.([a-z]+)$/i);
    const extension = extMatch ? extMatch[1].toLowerCase() : '';
    const isValid = entityCode !== null && runNumber !== null && month !== null && year !== null;
    return {
        entityCode,
        fileType,
        runNumber,
        month,
        year,
        extension,
        isValid,
        rawFilename: filename
    };
}
/**
 * Group files by entity code.
 */
function groupFilesByEntity(filenames) {
    const groups = new Map();
    for (const filename of filenames) {
        const entityCode = extractEntityCode(filename);
        if (entityCode) {
            const existing = groups.get(entityCode) || [];
            existing.push(filename);
            groups.set(entityCode, existing);
        }
    }
    return groups;
}
/**
 * Convert month abbreviation (Jan, Feb, etc.) to YYYY-MM format.
 */
function formatPeriodMonth(month, year) {
    if (!month || !year)
        return null;
    const monthMap = {
        jan: '01', feb: '02', mar: '03', apr: '04',
        may: '05', jun: '06', jul: '07', aug: '08',
        sep: '09', oct: '10', nov: '11', dec: '12'
    };
    const monthNum = monthMap[month.toLowerCase()];
    if (!monthNum)
        return null;
    const fullYear = year.length === 2 ? `20${year}` : year;
    return `${fullYear}-${monthNum}`;
}
/**
 * Entity-code extraction for EMAIL-INGESTED filenames.
 *
 * Different rule from {@link extractEntityCode}: an emailed attachment carries
 * its code anywhere in the name, not only as a leading anchor
 * (`RCTI-ENTO100-20260101.pdf`, `COMM_STMT_TCSF100-MASTER-1.csv`). It uses
 * `(?:^|[^A-Za-z0-9])` rather than `\b` because underscore is a word character
 * in JS regex, so `\b` never fires on the sanitized forms these paths receive.
 *
 * Consolidated 2026-08-28 from two byte-identical inline copies:
 *   services/admin-functions/src/email-ingestion/uploadExtractedFile.ts
 *   services/admin-functions/src/email-ingestion/syncEmailConnection.ts
 * The second one's own comment described itself as a copy of the first, which
 * is exactly how this rule drifted the first time.
 *
 * DOT FIX. The generic fallback previously used `[A-Z][A-Z0-9]{2,9}` with a
 * `[^A-Za-z0-9]` terminator, and a dot satisfies that terminator. So
 * `MAVI1.00 AGMT RUN 3 Jul 23.xlsx` captured `MAVI1` and returned a WRONG code
 * rather than null. That is worse than the 2026-08-18 NULL defect: a null is
 * caught by the 07:00 validateUnifiedEntityCodeCoverage scan within a day,
 * whereas `MAVI1` passes it and silently disappears from every loan-book
 * scoped query. Dots are now admitted into the class and stripped.
 *
 * The quantifier stays {2,9} so an UNDOTTED name matches exactly the same span
 * as before: this is provably zero-blast except for dotted anchors.
 */
function extractEntityCodeFromEmailFilename(filename) {
    // Pattern 1: Entourage entity codes (ENTO100, ENTM100, ENBB100, etc.)
    const entityMatch = filename.match(/(?:^|[^A-Za-z0-9])(EN[A-Z]{1,2}\d{3})(?:[^A-Za-z0-9]|$)/i);
    if (entityMatch)
        return entityMatch[1].toUpperCase();
    // Pattern 2: V-codes (V316295PL, V02974PL, etc.)
    const vcodeMatch = filename.match(/(?:^|[^A-Za-z0-9])(V\d+PL)(?:[^A-Za-z0-9]|$)/i);
    if (vcodeMatch)
        return vcodeMatch[1].toUpperCase();
    // Pattern 3: ENTORS
    const entorsMatch = filename.match(/(?:^|[^A-Za-z0-9])(ENTORS)(?:[^A-Za-z0-9]|$)/i);
    if (entorsMatch)
        return 'ENTORS';
    // Pattern 4: ENTORUM
    const entorumMatch = filename.match(/(?:^|[^A-Za-z0-9])(ENTORUM)(?:[^A-Za-z0-9]|$)/i);
    if (entorumMatch)
        return 'ENTORUM';
    // Generic fallback for unified-mode tenants (TCSF100, AMAG-VFA, MAVI1.00).
    const genericMatch = filename.match(/(?:^|[^A-Za-z0-9])([A-Z][A-Z0-9.]{2,9})(?:[^A-Za-z0-9]|$)/);
    if (genericMatch) {
        // Dots are cosmetic: MAVI1.00 and MAVI100 are the same entity.
        const stripped = genericMatch[1].replace(/\./g, '');
        if (stripped.length >= 3)
            return stripped.toUpperCase();
    }
    return null;
}
